import type { Section } from '../types';

/**
 * Guest shell strings. English is the default and is written as spoken prose:
 * warm, confident, unhurried, family forward. Spanish is the toggle.
 */
export const guest = {
  'guest.nav.arrive': { es: 'Llegada', en: 'Arrive' },
  // Was "Your stay" / "Tu estadia", the old home screen's name before the
  // tile home replaced it (2026-09-24 redesign). Every back link on every
  // screen uses this key by default, so it went stale everywhere at once.
  'guest.nav.stay': { es: 'Inicio', en: 'Home' },
  'guest.nav.houses': { es: 'Las casas', en: 'House guides' },
  'guest.nav.island': { es: 'La isla', en: 'Island guide' },
  'guest.nav.requests': { es: 'Pedidos', en: 'Requests' },
  'guest.nav.messages': { es: 'Mensajes', en: 'Messages' },
  'guest.nav.weather': { es: 'El clima', en: 'Weather' },
  'guest.nav.safety': { es: 'Seguridad', en: 'Safety' },
  'guest.nav.translator': { es: 'Traductor', en: 'Translator' },

  'guest.login.title': { es: 'Entra a tu estadía', en: 'Open your stay' },
  'guest.login.code_label': { es: 'ID de reserva', en: 'Reservation ID' },
  'guest.login.code_help': {
    es: 'Ocho caracteres, del correo de tu reserva.',
    en: 'Eight characters, from your booking email.',
  },
  'guest.login.lastname_label': { es: 'Apellido', en: 'Last name' },
  'guest.login.submit': { es: 'Entrar', en: 'Open my stay' },
  'guest.login.failed': {
    es: 'Ese ID de reserva y ese apellido no coinciden. Revísalos y prueba otra vez.',
    en: 'That Reservation ID and last name do not match. Check them and try again.',
  },
  'guest.login.public_only': {
    es: 'Tu estadía todavía no empieza, así que por ahora ves la llegada y la isla.',
    en: 'Your stay has not started yet, so for now you can read Arrive and the island guide.',
  },

  'guest.consent.title': { es: 'Tus datos', en: 'Your information' },
  'guest.consent.body': {
    es: 'Guardamos tu nombre, las fechas de tu estadía y lo que nos pidas, solo para atenderte durante tu visita. Puedes pedir una copia o que lo borremos cuando quieras.',
    en: 'We keep your name, your stay dates, and anything you ask us for, only to look after you during your visit. You can ask for a copy or ask us to delete it at any time.',
  },
  'guest.consent.accept': { es: 'De acuerdo, continuar', en: 'I agree, continue' },
  'guest.consent.decline': { es: 'Ahora no', en: 'Not now' },

  'guest.stay.tier.all_inclusive': { es: 'Todo incluido', en: 'All-Inclusive' },
  'guest.stay.tier.standard': { es: 'Estándar', en: 'Standard' },
  'guest.stay.dates': { es: 'Fechas', en: 'Dates' },
  'guest.stay.wifi': { es: 'Wi-Fi', en: 'Wi-Fi' },
  'guest.stay.quiet_hours': { es: 'Horas de silencio', en: 'Quiet hours' },
  'guest.stay.crew_hours': { es: 'Horario del equipo', en: 'When the crew is here' },

  'guest.request.title': { es: 'Pídenos algo', en: 'Ask us for something' },
  'guest.request.type.boat': { es: 'Un viaje en lancha', en: 'A boat run' },
  'guest.request.type.provisioning': { es: 'Compras', en: 'Provisioning' },
  'guest.request.type.maintenance': { es: 'Algo se dañó', en: 'Something is broken' },
  'guest.request.type.housekeeping': { es: 'Limpieza', en: 'Housekeeping' },
  'guest.request.type.concierge': { es: 'Un plan o una reserva', en: 'Plans and bookings' },
  'guest.request.type.other': { es: 'Otra cosa', en: 'Something else' },
  'guest.request.text_label': { es: 'Cuéntanos', en: 'Tell us' },
  'guest.request.submit': { es: 'Enviar solicitud', en: 'Send request' },
  'guest.request.sent': {
    es: 'Lo recibimos. El equipo te responde pronto.',
    en: 'We have it. Your hosts will come back to you shortly.',
  },
  'guest.request.whatsapp_hint': {
    es: 'Si prefieres hablar, escríbenos por WhatsApp.',
    en: 'If you would rather talk it through, message your hosts on WhatsApp.',
  },

  'guest.weather.title': { es: 'El clima', en: 'Weather' },
  'guest.weather.stale': {
    es: 'Este dato es viejo. Vuelve cuando haya señal.',
    en: 'This reading is old. Check again when you have signal.',
  },

  'guest.safety.title': { es: 'Seguridad', en: 'Safety' },
  'guest.safety.clinic': { es: 'Hospital', en: 'Hospital' },
  'guest.safety.lightning': {
    es: 'Si hay truenos, sal de la piscina y de los muelles.',
    en: 'If you hear thunder, come out of the pool and off the docks.',
  },
  /* Home: Your stay ------------------------------------------------------- */
  'guest.home.welcome': { es: 'Te damos la bienvenida, {name}', en: 'Welcome, {name}' },
  'guest.home.phase.upcoming': {
    es: 'Tu llegada se acerca. Lee la llegada con calma y llévala guardada en el teléfono.',
    en: 'Your arrival is close. Read Arrive at your own pace and keep it saved on your phone.',
  },
  'guest.home.phase.in_house': {
    es: 'Ya estás en la isla. Todo lo que necesitas está aquí, incluso sin señal.',
    en: 'You are on the island. Everything you need is here, even with no signal.',
  },
  'guest.home.phase.departed': {
    es: 'Gracias por quedarte con nosotros. Tu estadía sigue abierta por un tiempo.',
    en: 'Thank you for staying with us. Your stay page stays open for a little while.',
  },
  'guest.home.phase.planning': {
    es: 'Tu estadía todavía no abre. Por ahora puedes leer la llegada y la isla.',
    en: 'Your stay page has not opened yet. For now you can read Arrive and the island guide.',
  },
  'guest.home.phase.closed': {
    es: 'Esta estadía ya cerró. Puedes seguir leyendo la llegada y la isla.',
    en: 'This stay has closed. You can still read Arrive and the island guide.',
  },
  'guest.home.arrives_in': {
    es: 'Llegas en {count} días',
    en: 'You arrive in {count} days',
  },
  'guest.home.arrives_tomorrow': { es: 'Llegas mañana', en: 'You arrive tomorrow' },
  'guest.home.arrives_today': { es: 'Llegas hoy', en: 'You arrive today' },
  'guest.home.offline_tip': {
    es: 'Abre la llegada una vez con señal, en casa, y se queda guardada en tu teléfono.',
    en: 'Open Arrive once with signal, at home, and it stays saved on your phone.',
  },
  'guest.home.guides': { es: 'Guías', en: 'Guides' },
  'guest.home.asks': { es: 'Con tus anfitriones', en: 'With your hosts' },
  'guest.home.row.arrive': {
    es: 'Del aeropuerto al muelle y la primera hora.',
    en: 'From the airport to the dock, and the first hour.',
  },
  'guest.home.row.houses': {
    es: 'Cómo funciona cada casa y cada espacio.',
    en: 'How each house and space works.',
  },
  'guest.home.row.island': {
    es: 'Un mapa, los muelles y dónde sale y se pone el sol.',
    en: 'A map, the docks, and where the sun rises and sets.',
  },
  'guest.home.row.weather': {
    es: 'Ahora y los próximos tres días.',
    en: 'Right now and the next three days.',
  },
  'guest.home.row.safety': {
    es: 'Números de emergencia y qué hacer si alguien se lastima.',
    en: 'Emergency numbers, and what to do if someone is hurt.',
  },
  'guest.home.row.requests': {
    es: 'Un viaje en lancha, compras, algo que arreglar.',
    en: 'A boat run, provisioning, something to fix.',
  },
  'guest.home.row.messages': {
    es: 'Un hilo con tus anfitriones, con traducción.',
    en: 'A thread with your hosts, with translation.',
  },
  'guest.home.row.translator': {
    es: 'Escribe en un idioma y léelo en el otro.',
    en: 'Write in one language and read it in the other.',
  },
  'guest.home.locked_before': {
    es: 'Se abre 30 días antes de tu llegada.',
    en: 'Opens 30 days before you arrive.',
  },
  'guest.home.locked_after': {
    es: 'Se cerró 30 días después de tu salida.',
    en: 'Closed 30 days after you leave.',
  },
  'guest.home.locked_cancelled': {
    es: 'No está disponible para esta reserva.',
    en: 'Not available for this booking.',
  },
  'guest.stay.title': { es: 'Tu estadía', en: 'Your stay' },
  'guest.stay.dates_range': { es: 'Del {from} al {to}', en: '{from} to {to}' },
  'guest.stay.party': {
    es: '{adults} adultos y {children} niños',
    en: '{adults} adults and {children} children',
  },
  'guest.stay.boat_time': { es: 'Hora de tu lancha', en: 'Your boat time' },
  'guest.stay.boat_time_unset': {
    es: 'Tus anfitriones confirman la hora de la lancha antes de tu viaje.',
    en: 'Your hosts confirm your boat time before you travel.',
  },
  'guest.stay.included': { es: 'Lo que incluye tu estadía', en: 'What your stay includes' },
  'guest.stay.details': { es: 'Lo práctico', en: 'The practical bits' },
  'guest.stay.sleeps': {
    es: 'Aquí duermen hasta 24 personas y la isla es toda para tu grupo.',
    en: 'The island sleeps up to 24 and is all yours.',
  },

  /* Consent: Ley 81 --------------------------------------------------------- */
  'guest.consent.points': {
    es: 'Lo que guardamos: tu nombre, las fechas, tus pedidos y tus mensajes. Lo que no: nada de cámaras en las áreas de huéspedes, nada de tu ubicación.',
    en: 'What we keep: your name, your dates, your requests and your messages. What we do not: no cameras in guest areas, no tracking of your location.',
  },
  'guest.consent.law': {
    es: 'La Ley 81 de 2019 de Panamá pide tu permiso claro antes de guardar datos personales. Sin él, puedes leer la llegada y la isla sin entrar.',
    en: 'Panama’s Ley 81 of 2019 asks for your clear yes before we keep personal data. Without it, you can still see how to get to the island and read the island guide without signing in.',
  },
  'guest.consent.required': {
    es: 'Marca la casilla para continuar.',
    en: 'Tick the box to continue.',
  },
  'guest.login.read_arrive': {
    es: 'Ver cómo llegar a la isla (sin entrar)',
    en: 'See how to get to the island (no sign-in needed)',
  },
  'guest.login.intro': {
    es: 'Con el ID de reserva que llegó en tu correo abres tu estadía completa.',
    en: 'The Reservation ID from your booking email opens your whole stay.',
  },

  /* Arrive ------------------------------------------------------------------ */
  'guest.arrive.title': { es: 'Llegada a la isla', en: 'Arriving on the island' },
  'guest.arrive.lede': {
    es: 'Se llega en lancha, unos quince minutos desde Bocas Town, y es la parte que todos recuerdan.',
    en: 'You arrive by boat, about fifteen minutes from Bocas Town, and it is the part everyone remembers.',
  },
  'guest.arrive.photo_alt': {
    es: 'Foto de la isla vista desde el agua (próximamente)',
    en: 'Photo of the island seen from the water (to come)',
  },
  'guest.arrive.steps_intro': { es: 'El camino, paso a paso', en: 'The way in, one step at a time' },
  'guest.arrive.good_to_know': { es: 'Bueno saber antes de viajar', en: 'Good to know before you travel' },
  'guest.arrive.step': { es: 'Paso {n}', en: 'Step {n}' },
  'guest.arrive.save_offline': {
    es: 'Guardada en tu teléfono para leerla sin señal.',
    en: 'Saved on your phone to read with no signal.',
  },
  'guest.arrive.open_stay': { es: 'Abrir mi estadía', en: 'Open my stay' },
  'guest.arrive.sign_in': { es: 'Entrar con mi ID de reserva', en: 'Sign in with my Reservation ID' },

  /* House guides ------------------------------------------------------------ */
  'guest.houses.title': { es: 'Guías de las casas', en: 'House guides' },
  'guest.houses.lede': {
    es: 'Una página por casa y por espacio. El código QR de cada casa abre la suya.',
    en: 'One page for each house and space. The QR code in each house opens its own.',
  },
  'guest.houses.everywhere': { es: 'Para toda la isla', en: 'For the whole island' },
  'guest.houses.spaces': { es: 'Casas y espacios', en: 'Houses and spaces' },
  'guest.houses.not_found': {
    es: 'No encontramos esa guía. Mira la lista de guías.',
    en: 'We could not find that guide. Have a look at the list of guides.',
  },
  'guest.houses.all': { es: 'Todas las guías', en: 'All guides' },

  /* Island guide ------------------------------------------------------------ */
  'guest.island.title': { es: 'La isla', en: 'The island' },
  'guest.island.lede': {
    es: 'Un croquis para orientarte. No está a escala.',
    en: 'A sketch to get your bearings. It is not to scale.',
  },
  'guest.island.map_label': {
    es: 'Croquis de la isla con las casas, los muelles, el manglar y los lados del sol',
    en: 'Sketch of the island with the houses, the docks, the mangrove edge and the sun sides',
  },
  'guest.island.map_note': {
    es: 'Croquis, no a escala. Las posiciones son aproximadas.',
    en: 'A sketch, not to scale. Positions are illustrative.',
  },
  'guest.island.label.hacienda': { es: 'La hacienda', en: 'The hacienda' },
  'guest.island.label.bungalow': { es: 'El bungalow', en: 'The bungalow' },
  'guest.island.label.pavilion': { es: 'El pabellón solar', en: 'The solar pavilion' },
  'guest.island.label.docks': { es: 'Los muelles', en: 'The docks' },
  'guest.island.label.pool': { es: 'Piscina y jacuzzi', en: 'Pool and hot tub' },
  'guest.island.label.mangrove': { es: 'El manglar', en: 'The mangrove edge' },
  'guest.island.label.sunrise': { es: 'Sale el sol', en: 'Sunrise' },
  'guest.island.label.sunset': { es: 'Se pone el sol', en: 'Sunset' },
  'guest.island.label.east': { es: 'Este', en: 'East' },
  'guest.island.label.west': { es: 'Oeste', en: 'West' },
  'guest.island.label.water': { es: 'Mar abierto', en: 'Open water' },

  /* Requests ---------------------------------------------------------------- */
  'guest.request.lede': {
    es: 'Dinos lo que necesitas y el equipo lo ve en su lista. Para conversar, mejor WhatsApp.',
    en: 'Tell us what you need and your hosts see it on their list. To talk it through, use WhatsApp.',
  },
  'guest.request.type_label': { es: '¿Qué necesitas?', en: 'What do you need?' },
  'guest.request.when_label': { es: '¿Para cuándo? (opcional)', en: 'By when? (optional)' },
  'guest.request.when_help': {
    es: 'Hora de la isla, Panamá.',
    en: 'Island time, Panama.',
  },
  'guest.request.text_help': {
    es: 'Cuántos son, dónde estás, lo que ayude. Puedes escribir en español o en inglés.',
    en: 'How many of you, where you are, whatever helps. Write in English or Spanish.',
  },
  'guest.request.saved': {
    es: 'Guardado en tu teléfono. Se envía solo en cuanto haya señal.',
    en: 'Saved on your phone. It sends itself as soon as there is signal.',
  },
  'guest.request.whatsapp_button': {
    es: 'Escribir a tus anfitriones por WhatsApp',
    en: 'Message your hosts on WhatsApp',
  },
  'guest.request.whatsapp_greeting': {
    es: 'Hola, escribimos desde Dia Bonito Solarte',
    en: 'Hello, writing from Dia Bonito Solarte',
  },
  'guest.request.empty_text': {
    es: 'Escribe unas palabras para que sepamos qué hacer.',
    en: 'Write a few words so we know what to do.',
  },
  'guest.request.mine': { es: 'Tus pedidos', en: 'Your requests' },
  'guest.request.mine_empty': {
    es: 'Cuando envíes uno, aparece aquí con su estado.',
    en: 'When you send one, it shows up here with its status.',
  },
  'guest.request.status.new': { es: 'Recibido', en: 'Received' },
  'guest.request.status.seen': { es: 'Visto', en: 'Seen' },
  'guest.request.status.in_progress': { es: 'Ya estamos en eso', en: 'On it' },
  'guest.request.status.done': { es: 'Listo', en: 'Done' },
  'guest.request.status.declined': { es: 'No se pudo', en: 'Could not do' },
  'guest.request.locked': {
    es: 'Los pedidos abren 30 días antes de tu llegada y cierran 30 días después de tu salida.',
    en: 'Requests open 30 days before you arrive and close 30 days after you leave.',
  },
  'guest.request.sign_in_first': {
    es: 'Entra con tu ID de reserva para enviar un pedido.',
    en: 'Sign in with your Reservation ID to send a request.',
  },

  /* Mensajes ---------------------------------------------------------------- */
  'guest.messages.title': { es: 'Mensajes', en: 'Messages' },
  'guest.messages.lede': {
    es: 'Un tablero con tus anfitriones, no un chat. Las respuestas no son al instante. Para algo urgente, WhatsApp.',
    en: 'A message board with your hosts, not a chat. Replies are not instant. For anything urgent, use WhatsApp.',
  },
  'guest.messages.empty': {
    es: 'Aún no hay mensajes. Escribe el primero y tus anfitriones lo leen en su idioma.',
    en: 'No messages yet. Write the first one and your hosts read it in their own language.',
  },
  'guest.messages.label': { es: 'Tu mensaje', en: 'Your message' },
  'guest.messages.send': { es: 'Enviar mensaje', en: 'Send message' },
  'guest.messages.you': { es: 'Tú', en: 'You' },
  'guest.messages.hosts': { es: 'Tus anfitriones', en: 'Your hosts' },
  'guest.messages.checked': { es: 'Revisado hace {ago}', en: 'Checked {ago} ago' },
  'guest.messages.offline': {
    es: 'Sin señal. Ves lo último que se guardó y tus mensajes nuevos se envían solos.',
    en: 'No signal. You see what was last saved, and your new messages send themselves.',
  },
  'guest.messages.urgent': { es: 'Para algo urgente', en: 'For anything urgent' },
  'guest.messages.locked': {
    es: 'Los mensajes abren 30 días antes de tu llegada y cierran 30 días después de tu salida.',
    en: 'Messages open 30 days before you arrive and close 30 days after you leave.',
  },
  'guest.messages.sign_in_first': {
    es: 'Entra con tu ID de reserva para ver tus mensajes.',
    en: 'Sign in with your Reservation ID to see your messages.',
  },
  'guest.messages.no_translation': {
    es: 'Sin traducción todavía',
    en: 'Not translated yet',
  },
  'guest.messages.translated': { es: 'Traducido', en: 'Translated' },

  /* Traductor --------------------------------------------------------------- */
  'guest.translator.title': { es: 'Traductor', en: 'Translator' },
  'guest.translator.lede': {
    es: 'Escribe en un idioma y léelo en el otro. Sirve para hablar con el equipo, el capitán o quien te ayude.',
    en: 'Write in one language and read it in the other. Handy with the crew, the captain, or anyone helping you.',
  },
  'guest.translator.input_label': { es: 'Lo que quieres decir', en: 'What you want to say' },
  'guest.translator.direction.en_es': { es: 'Inglés a español', en: 'English to Spanish' },
  'guest.translator.direction.es_en': { es: 'Español a inglés', en: 'Spanish to English' },
  'guest.translator.swap': { es: 'Cambiar de sentido', en: 'Switch direction' },
  'guest.translator.go': { es: 'Traducir', en: 'Translate' },
  'guest.translator.result': { es: 'Traducción', en: 'Translation' },
  'guest.translator.copy': { es: 'Copiar', en: 'Copy' },
  'guest.translator.copied': { es: 'Copiado', en: 'Copied' },
  'guest.translator.whatsapp': { es: 'Enviar por WhatsApp', en: 'Send on WhatsApp' },
  'guest.translator.offline': {
    es: 'No hay señal, así que no se puede traducir ahora. Tu texto sigue aquí, inténtalo cuando vuelva.',
    en: 'There is no signal, so nothing can be translated right now. Your text is still here. Try again when it returns.',
  },
  'guest.translator.quota': {
    es: 'Se acabaron las traducciones de tu estadía. Escribe en tu idioma y tus anfitriones te entienden.',
    en: 'The translations for your stay are used up. Write in your own language and your hosts will manage.',
  },
  'guest.translator.failed': {
    es: 'No se pudo traducir ahora. Tu texto sigue aquí, inténtalo de nuevo en un momento.',
    en: 'The translation did not work just now. Your text is still here. Try again in a moment.',
  },
  'guest.translator.recent': { es: 'Traducciones recientes', en: 'Recent translations' },
  'guest.translator.recent_note': {
    es: 'Se guardan en tu teléfono y las ves sin señal.',
    en: 'They are saved on your phone and you can read them with no signal.',
  },
  'guest.translator.clear': { es: 'Borrar la lista', en: 'Clear the list' },
  'guest.translator.locked': {
    es: 'El traductor abre 30 días antes de tu llegada y cierra 30 días después de tu salida.',
    en: 'The translator opens 30 days before you arrive and closes 30 days after you leave.',
  },
  'guest.translator.sign_in_first': {
    es: 'Entra con tu ID de reserva para usar el traductor.',
    en: 'Sign in with your Reservation ID to use the translator.',
  },

  /* Safety ------------------------------------------------------------------ */
  'guest.safety.lede': {
    es: 'Lo primero, en orden. Esta página funciona sin señal.',
    en: 'The first things, in order. This page works with no signal.',
  },
  'guest.safety.crew_title': { es: 'Llama a tus anfitriones', en: 'Call your hosts' },
  'guest.safety.crew_hint': {
    es: 'A cualquier hora. Ellos coordinan la lancha y el hospital si hace falta.',
    en: 'Any hour. They coordinate the boat and the hospital if you need them.',
  },
  'guest.safety.crew_pending': { es: 'Línea directa con tus anfitriones: próximamente.', en: 'A direct line to your hosts: coming soon.' },

  /* Weather ----------------------------------------------------------------- */
  'guest.weather.lede': {
    es: 'El pronóstico para la isla. Cuando no hay señal, ves el último guardado y cuánto tiempo tiene.',
    en: 'The forecast for the island. With no signal, you see the last one saved and how old it is.',
  },
  'guest.weather.now': { es: 'Ahora', en: 'Right now' },
  'guest.weather.next_days': { es: 'Los próximos tres días', en: 'The next three days' },
  'guest.weather.temp': { es: '{temp} °C / {tempF} °F', en: '{temp} °C / {tempF} °F' },
  'guest.weather.humidity': { es: 'Humedad {pct} %', en: 'Humidity {pct} %' },
  'guest.weather.wind': { es: 'Viento {kph} km/h', en: 'Wind {kph} km/h' },
  'guest.weather.gusts': { es: 'ráfagas de {kph} km/h', en: 'gusts to {kph} km/h' },
  'guest.weather.high_low': { es: 'Máxima {high} °C, mínima {low} °C', en: 'High {high} °C, low {low} °C' },
  'guest.weather.rain_chance': { es: 'Lluvia: {pct} %', en: 'Rain: {pct} %' },
  'guest.weather.today': { es: 'Hoy', en: 'Today' },
  'guest.weather.updated_now': { es: 'Actualizado ahora mismo', en: 'Updated just now' },
  'guest.weather.updated_minutes': { es: 'Actualizado hace {count} min', en: 'Updated {count} min ago' },
  'guest.weather.updated_hours': { es: 'Actualizado hace {count} h', en: 'Updated {count} h ago' },
  'guest.weather.updated_days': { es: 'Actualizado hace {count} días', en: 'Updated {count} days ago' },
  'guest.weather.offline': {
    es: 'Sin señal. Esto es lo último que guardamos.',
    en: 'No signal. This is the last forecast we saved.',
  },
  'guest.weather.none': {
    es: 'Todavía no hay un pronóstico guardado. Se descarga la primera vez que abres esta página con señal.',
    en: 'There is no forecast saved yet. It downloads the first time you open this page with signal.',
  },
  'guest.weather.thunder': {
    es: 'Hay tormenta eléctrica en el pronóstico. Si oyes truenos, sal de la piscina y de los muelles.',
    en: 'There is thunder in the forecast. If you hear it, come out of the pool and off the docks.',
  },
  'guest.weather.source': {
    es: 'Datos de Open-Meteo para la isla.',
    en: 'Data from Open-Meteo for the island.',
  },
  'guest.weather.kind.clear': { es: 'Despejado', en: 'Clear' },
  'guest.weather.kind.partly': { es: 'Parcialmente nublado', en: 'Partly cloudy' },
  'guest.weather.kind.cloudy': { es: 'Nublado', en: 'Cloudy' },
  'guest.weather.kind.fog': { es: 'Neblina', en: 'Fog' },
  'guest.weather.kind.drizzle': { es: 'Llovizna', en: 'Drizzle' },
  'guest.weather.kind.rain': { es: 'Lluvia', en: 'Rain' },
  'guest.weather.kind.showers': { es: 'Aguaceros', en: 'Showers' },
  'guest.weather.kind.thunder': { es: 'Tormenta eléctrica', en: 'Thunderstorm' },
  'guest.weather.kind.unknown': { es: 'Sin dato', en: 'No reading' },
  'guest.weather.refresh': { es: 'Actualizar', en: 'Refresh' },

  // Room Referee: who sleeps where. Appended for migration 016, nothing above
  // this line reworded.
  'guest.nav.rooms': { es: 'Quién duerme dónde', en: 'Who sleeps where' },

  'guest.home.row.rooms': {
    es: 'Escoge tu cuarto antes de llegar.',
    en: 'Pick your room before you arrive.',
  },

  'guest.rooms.title': { es: 'Quién duerme dónde', en: 'Who sleeps where' },
  'guest.rooms.lede': {
    es: 'Escoge tu cuarto. El primero que lo toma se queda con él.',
    en: 'Pick your room. First come, first served.',
  },
  'guest.rooms.progress': {
    es: '{placed} de {people} personas ya tienen cuarto',
    en: '{placed} of {people} people have a room',
  },
  'guest.rooms.rooms_used': {
    es: '{used} de {total} cuartos en uso',
    en: '{used} of {total} bedrooms in use',
  },
  'guest.rooms.house_hacienda': { es: 'La hacienda', en: 'The hacienda' },
  'guest.rooms.floor.lower': { es: 'Planta baja', en: 'Lower level' },
  'guest.rooms.floor.upper': { es: 'Planta alta', en: 'Upstairs' },
  'guest.rooms.floor.main': { es: 'Planta principal', en: 'Main level' },
  'guest.rooms.en_suite': { es: 'Baño completo dentro del cuarto', en: 'En-suite full bath' },
  'guest.rooms.bed_line': { es: '{count} x {bed}', en: '{count} x {bed}' },
  'guest.rooms.bed_extra': {
    es: 'se abre para dos personas más',
    en: 'opens for two more people',
  },
  'guest.rooms.sleeps': {
    es: 'Para {base} personas, hasta {max} con el futón',
    en: 'Sleeps {base}, up to {max} with the futon',
  },
  'guest.rooms.sleeps_plain': { es: 'Para {max} personas', en: 'Sleeps {max}' },
  'guest.rooms.free': { es: '{count} lugares libres', en: '{count} spots open' },
  'guest.rooms.free_one': { es: 'Queda 1 lugar', en: '1 spot open' },
  'guest.rooms.full': { es: 'Lleno', en: 'Full' },
  'guest.rooms.photo_missing': {
    es: '[FOTO POR ENVIAR] Todavía no hay foto de este cuarto.',
    en: '[PHOTO TO SUPPLY] There is no photograph of this room yet.',
  },
  'guest.rooms.photo_alt': { es: 'Foto de {room}', en: 'Photograph of {room}' },
  'guest.rooms.claim': { es: 'Quedarme con este cuarto', en: 'Take this room' },
  'guest.rooms.claim_for': { es: 'Poner a alguien aquí', en: 'Put someone here' },
  'guest.rooms.move_here': { es: 'Cambiarme a este cuarto', en: 'Move me here' },
  'guest.rooms.release': { es: 'Dejar el cuarto', en: 'Give up the room' },
  'guest.rooms.release_person': { es: 'Sacar a {name}', en: 'Take {name} out' },
  'guest.rooms.in_this_room': { es: 'En este cuarto', en: 'In this room' },
  'guest.rooms.you': { es: 'tú', en: 'you' },
  'guest.rooms.booker': { es: 'organiza el viaje', en: 'booked the trip' },
  'guest.rooms.nobody_yet': { es: 'Nadie todavía.', en: 'Nobody yet.' },
  'guest.rooms.mine': { es: 'Tu cuarto: {room}', en: 'Your room: {room}' },
  'guest.rooms.mine_none': {
    es: 'Todavía no has escogido cuarto.',
    en: 'You have not picked a room yet.',
  },
  'guest.rooms.unplaced': {
    es: 'Sin cuarto todavía: {names}',
    en: 'Still without a room: {names}',
  },
  'guest.rooms.pick_who': { es: '¿Para quién?', en: 'Who is it for?' },

  'guest.rooms.deadline_none': {
    es: 'No hay fecha límite para escoger.',
    en: 'There is no deadline for picking.',
  },
  'guest.rooms.deadline_open': {
    es: 'Quedan {hours} horas para escoger cuarto.',
    en: '{hours} hours left to pick a room.',
  },
  'guest.rooms.deadline_passed': {
    es: 'Se acabó el tiempo de escoger. Quien organiza el viaje puede acomodar al resto.',
    en: 'The time to pick has passed. Whoever booked the trip can place the rest.',
  },
  'guest.rooms.deadline_set': { es: 'Poner fecha límite', en: 'Set a deadline' },
  'guest.rooms.deadline_clear': { es: 'Quitar la fecha límite', en: 'Remove the deadline' },
  'guest.rooms.deadline_help': {
    es: 'Opcional. Solo quien organiza el viaje la pone.',
    en: 'Optional, and only whoever booked the trip can set it.',
  },

  'guest.rooms.offline': {
    es: 'Sin señal. Escoger cuarto necesita señal, porque el primero que llega se queda con él. Intenta otra vez cuando vuelva.',
    en: 'No signal. Picking a room needs signal, because it is first come first served. Try again when it comes back.',
  },
  'guest.rooms.error_full': {
    es: 'Alguien tomó ese cuarto primero. Escoge otro.',
    en: 'Somebody took that room first. Pick another one.',
  },
  'guest.rooms.error_deadline': {
    es: 'Se acabó el tiempo de escoger. Pídeselo a quien organiza el viaje.',
    en: 'The time to pick has passed. Ask whoever booked the trip.',
  },
  'guest.rooms.error_not_yours': {
    es: 'Solo quien organiza el viaje puede mover a otra persona.',
    en: 'Only whoever booked the trip can move somebody else.',
  },
  'guest.rooms.error_generic': {
    es: 'No se pudo guardar. Intenta otra vez.',
    en: 'That did not save. Try again.',
  },

  'guest.rooms.party_title': { es: 'Quiénes vienen', en: 'Who is coming' },
  'guest.rooms.party_lede': {
    es: 'Quien organiza el viaje escribe los nombres del grupo.',
    en: 'Whoever booked the trip writes down the names of the group.',
  },
  'guest.rooms.party_add': { es: 'Agregar a alguien', en: 'Add someone' },
  'guest.rooms.first_name': { es: 'Nombre', en: 'First name' },
  'guest.rooms.last_name': { es: 'Apellido', en: 'Last name' },
  'guest.rooms.email': { es: 'Correo (opcional)', en: 'Email (optional)' },
  'guest.rooms.handle': { es: 'Red social (opcional)', en: 'Social handle (optional)' },
  'guest.rooms.contact_help': {
    es: 'El correo y la red social son opcionales. Dejarlos aquí no te apunta a ninguna lista: eso se hace en persona en la isla, si tú quieres.',
    en: 'Email and social handle are optional. Leaving them here does not sign you up for anything: that is done in person at the property, only if you want it.',
  },
  'guest.rooms.party_saved': { es: 'Guardado.', en: 'Saved.' },
  'guest.rooms.party_needs_name': {
    es: 'Escribe al menos el nombre y el apellido.',
    en: 'Write at least a first name and a last name.',
  },

  // Redesign phase 1: the tile home and the help button (docs/GUEST-REDESIGN.md).
  // Appended, nothing above reworded. Tile names are Set A.
  'guest.home.tiles_label': { es: 'Todo para tu estadía', en: 'Everything for your stay' },
  'guest.tile.locked': { es: 'Cerrado', en: 'Locked' },

  'guest.tile.arrival': { es: 'Guía de llegada', en: 'Arrival Guide' },
  'guest.tile.arrival_hint': {
    es: 'Los vuelos, el muelle, la primera hora',
    en: 'Flights, the dock, the first hour',
  },
  'guest.tile.arrival_boat': { es: 'La lancha sale a las {time}', en: 'Boat at {time}' },
  'guest.tile.arrival_open_once': {
    es: 'Ábrela una vez en casa y funciona sin señal',
    en: 'Open it once at home, it works with no signal',
  },
  'guest.tile.arrival_thanks': {
    es: 'Gracias por quedarte con nosotros',
    en: 'Thank you for staying with us',
  },
  'guest.tile.arrival_left': { es: 'Saliste el {date}', en: 'You left on {date}' },

  'guest.tile.house': { es: 'Guía de la casa', en: 'House Guide' },
  'guest.tile.house_hint': {
    es: 'El Wi-Fi, las reglas y cómo funciona todo',
    en: 'Wi-Fi, rules, how things work',
  },

  'guest.tile.island': { es: 'Guía de la isla', en: 'Island Guide' },
  'guest.tile.island_hint': {
    es: 'El mapa, el snorkel, el atardecer',
    en: 'The map, snorkeling, sunset',
  },

  'guest.tile.weather': { es: 'El clima', en: 'Weather' },
  'guest.tile.weather_live': { es: '{temp}, {sky}', en: '{temp}, {sky}' },
  'guest.tile.weather_hint': { es: 'Hoy y los próximos días', en: 'Today and the next few days' },
  'guest.tile.weather_none': {
    es: 'Ábrelo con señal para ver el pronóstico',
    en: 'Open it with signal for the forecast',
  },

  'guest.tile.translator': { es: 'Traductor', en: 'Translator' },
  'guest.tile.translator_hint': {
    es: 'Inglés y español, y las últimas se quedan guardadas',
    en: 'English and Spanish, the last few kept on your phone',
  },

  'guest.tile.rooms': { es: 'Quién duerme dónde', en: 'Who sleeps where' },
  'guest.tile.rooms_count': {
    es: '{used} de {total} cuartos escogidos',
    en: '{used} of {total} rooms picked',
  },
  'guest.tile.rooms_none': { es: 'Escoge tu cuarto', en: 'Pick your room' },
  'guest.tile.rooms_hint': {
    es: 'Escoge el tuyo, el primero que llega se queda con él',
    en: 'Pick yours, first come first served',
  },
  'guest.tile.rooms_offline': { es: 'Necesitas señal para escoger', en: 'Needs signal to pick' },

  'guest.rooms.screen_title': { es: 'Asignación de cuartos', en: 'Room assignments' },

  'guest.help.button': { es: 'Ayuda y seguridad', en: 'Help and safety' },
  'guest.help.button_unread': {
    es: 'Ayuda y seguridad. Tus anfitriones te respondieron.',
    en: 'Help and safety. Your hosts have replied.',
  },
  'guest.help.word': { es: 'Ayuda', en: 'Help' },
  'guest.help.title': { es: 'Ayuda', en: 'Help' },
  'guest.help.sub': { es: 'Tus anfitriones leen cada mensaje.', en: 'Your hosts read every message.' },
  'guest.help.crew_call': { es: '¿Una emergencia? Llama a tus anfitriones', en: 'Emergency? Call your hosts' },
  'guest.help.crew_call_hint': {
    es: 'A cualquier hora. Coordinan la lancha y el hospital si hace falta.',
    en: 'Any hour. They coordinate the boat and the hospital if you need them.',
  },
  'guest.help.crew_pending': { es: 'Línea directa con tus anfitriones: próximamente.', en: 'A direct line to your hosts: coming soon.' },
  'guest.help.safety': { es: 'Seguridad y primeros auxilios', en: 'Safety and first aid' },
  'guest.help.safety_hint': {
    es: 'A quién llamar, dónde está el botiquín, los rayos',
    en: 'Who to call, where the kit is, lightning',
  },
  'guest.help.ask': { es: 'Pedir algo', en: 'Ask for something' },
  'guest.help.ask_hint': {
    es: 'Un viaje en lancha, compras, limpieza',
    en: 'A boat run, groceries, housekeeping',
  },
  'guest.help.problem': { es: 'Reportar un problema', en: 'Report a problem' },
  'guest.help.problem_hint': {
    es: '¿Algo se dañó? Puedes agregar una foto',
    en: 'Something broken? Add a photo',
  },
  'guest.help.messages': { es: 'Mensajes', en: 'Messages' },
  'guest.help.messages_hint': {
    es: 'Tu conversación con los anfitriones',
    en: 'Your thread with the hosts',
  },
  'guest.help.unread': { es: '{count} sin leer', en: '{count} unread' },
  'guest.help.sign_in': { es: 'Entra para enviar', en: 'Sign in to send' },
  'guest.help.fineprint': {
    es: 'Los mensajes no son un chat en vivo. Escribe cuando te quede bien, traducimos en los dos sentidos y la respuesta llega aquí. Para lo que no puede esperar, llama.',
    en: 'Messages are not a live chat. Write when it suits you, we translate both ways, and the reply lands here. For anything that cannot wait, call.',
  },

  'guest.request.problem_title': { es: 'Reportar un problema', en: 'Report a problem' },
  'guest.request.problem_lede': {
    es: 'Cuéntanos qué se dañó y dónde. Una foto le ayuda mucho al equipo.',
    en: 'Tell us what is broken and where. A photo helps the crew a lot.',
  },
  'guest.request.photo_label': { es: 'Agregar una foto', en: 'Add a photo' },
  'guest.request.photo_help': {
    es: 'Es opcional. La foto sube apenas la tomas, y el reporte sale igual aunque todavía no haya llegado.',
    en: 'Optional. The photo uploads the moment you take it, and the report goes either way.',
  },

  /* House Guide, Touch Stay style (docs/GUEST-REDESIGN.md Section 5).
     Appended for Phase 2, nothing above this line reworded. The screen still
     answers to the same route, /casas, that the QR codes and the House Guide
     tile use. */
  'guest.guide.cover_title': { es: 'Bienvenidos a Dia Bonito Solarte', en: 'Welcome to Dia Bonito Solarte' },
  'guest.guide.cover_sub': {
    es: 'Duerme hasta 24 personas. Se llega solo en lancha, quince minutos desde Bocas Town.',
    en: 'Sleeps up to 24. Boat only, fifteen minutes from Bocas Town.',
  },
  'guest.guide.browse_title': { es: 'Explorar', en: 'Browse' },
  'guest.guide.topic_included': { es: 'Lo que incluye', en: 'What is included' },

  'guest.guide.checkout_title': { es: 'Salida', en: 'Check-out' },
  'guest.guide.checkout_sign_in': {
    es: 'Entra con tu ID de reserva para ver el día y la hora de tu salida.',
    en: 'Sign in with your Reservation ID to see your check-out day and time.',
  },
  'guest.guide.rules_title': { es: 'Reglas de la casa', en: 'House rules' },
  'guest.guide.included_sign_in': {
    es: 'Entra con tu ID de reserva para ver qué incluye tu estadía.',
    en: 'Sign in with your Reservation ID to see what your stay includes.',
  },

  'guest.guide.wifi_copy': { es: 'Copiar nombre', en: 'Copy name' },
  'guest.guide.wifi_copied': { es: 'Copiado', en: 'Copied' },

  'guest.guide.search_placeholder': { es: 'Buscar en la guía', en: 'Search the guide' },
  'guest.guide.search_results': {
    es: '{count} resultados para "{query}"',
    en: '{count} results for "{query}"',
  },
  'guest.guide.search_none': {
    es: 'No hay resultados. Prueba otra palabra.',
    en: 'Nothing matches. Try another word.',
  },

  'guest.guide.expand_all': { es: 'Abrir todo', en: 'Expand all' },
  'guest.guide.collapse_all': { es: 'Cerrar todo', en: 'Collapse all' },

  'guest.guide.rooms_heading': { es: 'Cuartos', en: 'Bedrooms' },
  'guest.guide.room_open': { es: 'Libre', en: 'Open' },
  'guest.guide.room_picked_by': { es: 'Lo tiene {name}', en: 'Picked by {name}' },
  'guest.guide.room_picked_more': {
    es: 'Lo tiene {name} y {count} más',
    en: 'Picked by {name} and {count} more',
  },
  'guest.guide.room_picked_generic': { es: 'Ya está tomado', en: 'Already picked' },
  'guest.guide.room_sign_in': {
    es: 'Entra para ver quién tiene este cuarto.',
    en: 'Sign in to see who has this room.',
  },

  /* One help thread (docs/GUEST-REDESIGN.md, Phase 3). Appended; nothing above
     this line reworded. Requests and Messages become one thread with the hosts,
     and "Ask for something" and "Report a problem" are two ways to start a line
     in it. */
  'guest.thread.title': { es: 'Tú y tus anfitriones', en: 'You and your hosts' },
  'guest.thread.lede': {
    es: 'Un solo hilo para todo: pedidos, problemas y mensajes. No es un chat en vivo. Tus anfitriones responden aquí, en tu idioma.',
    en: 'One thread for everything: requests, problems and messages. It is not a live chat. Your hosts answer here, in your language.',
  },
  'guest.thread.back': { es: 'Volver a Ayuda', en: 'Back to Help' },
  'guest.thread.empty': {
    es: 'Aún no hay nada aquí. Pide algo, reporta un problema o solo saluda, y tus anfitriones lo leen en su idioma.',
    en: 'Nothing here yet. Ask for something, report a problem, or just say hello, and your hosts read it in their own language.',
  },
  'guest.thread.mode_label': { es: '¿Qué quieres hacer?', en: 'What would you like to do?' },
  'guest.thread.mode.message': { es: 'Escribir un mensaje', en: 'Write a message' },
  'guest.thread.problem_submit': { es: 'Enviar el reporte', en: 'Send the report' },
  'guest.thread.message_sent': {
    es: 'Enviado. Tus anfitriones lo leen en su idioma.',
    en: 'Sent. Your hosts read it in their own language.',
  },
  'guest.thread.card_when': { es: 'Para {when}', en: 'For {when}' },
  'guest.thread.card_photos': { es: 'Fotos: {count}', en: 'Photos: {count}' },
  'guest.thread.reply_to': { es: 'Respuesta a tu pedido', en: 'Reply to your request' },
  'guest.thread.ask_notes': {
    es: 'Cómo funcionan las lanchas y las compras',
    en: 'How boat runs and groceries work',
  },
} as const satisfies Section;
